import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "./logo.png";
import "./style.css";

const Navbar = ({ setShowSearch, watchList }) => {
  const [sticky, setSticky] = useState(false);
  const [responsive, setResponsive] = useState(window.innerWidth < 820);
  const [showSide, setShowSide] = useState(false);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const searchRef = useRef(null);
  const typingTimer = useRef(null);
  const navigate = useNavigate();

  // Responsive state on resize
  useEffect(() => {
    const handleResize = () => setResponsive(window.innerWidth < 820);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sticky navbar
  useEffect(() => {
    const handleScroll = () => setSticky(window.scrollY > 245);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toggle body scroll when sidebar open
  useEffect(() => {
    document.body.style.overflow = showSide ? "hidden" : "auto";
  }, [showSide]);

  // Load user from localStorage
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      if (savedUser && savedUser !== "undefined") {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.error("Lỗi khi đọc user từ localStorage:", error);
    }
  }, []);

  // Click outside search input
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchResults([]);
        setIsTyping(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch kết quả tìm kiếm
  const fetchSearchResults = async (query) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/movies/search?keyword=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error("Lỗi khi gọi API tìm kiếm");
      const data = await res.json();
      setSearchResults(data.content || []);
    } catch (error) {
      console.error("Lỗi tìm kiếm:", error);
    }
  };

  // Đăng xuất
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("role");
    setUser(null);
    window.location.href = "/";
  };

  // Xử lý tìm kiếm submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearch(true);
    }
  };

  return (
    <nav className={sticky ? "navbar sticky" : "navbar"}>
      <div className="container">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="navbar-brand">
            <Link className="navbar-item link" to="/">
              <img src={logo} alt="Movflx" className="logo" />
            </Link>
          </div>
          <ul
            className={
              responsive
                ? showSide
                  ? "navbar-menu sidebar show"
                  : "navbar-menu sidebar"
                : "navbar-menu"
            }
          >
            {responsive && (
              <button
                className="btn close-btn"
                onClick={() => setShowSide(false)}
              >
                <i className="ri-close-line"></i>
              </button>
            )}
            <li className="navbar-item">
              <Link className="navbar-link" to="/" onClick={() => setShowSide(false)}>Trang Chủ</Link>
            </li>
            <li className="navbar-item">
              <Link className="navbar-link" to="/movies" onClick={() => setShowSide(false)}>Danh sách phim</Link>
            </li>
            <li className="navbar-item">
              <Link className="navbar-link SeeLater" to="/xem-sau" onClick={() => setShowSide(false)}>
                Xem sau{" "}
                {watchList.length ? (
                  <span className="num">{watchList.length}</span>
                ) : null}
              </Link>
            </li>
            <li className="navbar-item">
              <Link className="navbar-link" to="/gioi-thieu" onClick={() => setShowSide(false)}>Giới thiệu</Link>
            </li>

            {/* Ô tìm kiếm */}
            <li className="navbar-item search-container" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="search-form">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Tìm phim..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsTyping(true);
                    if (typingTimer.current) clearTimeout(typingTimer.current);
                    typingTimer.current = setTimeout(() => {
                      fetchSearchResults(e.target.value);
                    }, 300);
                  }}
                />
                <button type="submit" className="search-submit">
                  <i className="ri-search-line"></i>
                </button>
              </form>
              {isTyping && searchResults.length > 0 && (
                <ul className="search-suggestions">
                  {searchResults.map((movie) => (
                    <li
                      key={movie.id}
                      className="suggestion-item flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-100"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults([]);
                        setIsTyping(false);
                        navigate(`/movie/${movie.id}`);
                      }}
                    >
                      <img
                        src={movie.thumbnailUrl}
                        alt={movie.title}
                        className="w-10 h-14 object-cover rounded"
                      />
                      <span>{movie.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* User login/logout */}
            {!user ? (
              <li className="navbar-item">
                <Link className="navbar-link" to="/login" onClick={() => setShowSide(false)}>Login</Link>
              </li>
            ) : (
              <li
                className="navbar-item user-dropdown"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <span className="navbar-link username">
                  <i className="ri-user-line"></i> {user.username}{" "}
                  <i className="ri-arrow-down-s-line"></i>
                </span>
                {showDropdown && (
                  <ul className="dropdown-menu">
                    <li>
                      <Link to="/thong-tin-nguoi-dung" onClick={() => setShowSide(false)}>
                        Thông tin cá nhân
                      </Link>
                    </li>
                    <li>
                      <button className="logout" onClick={handleLogout}>Đăng xuất</button>
                    </li>
                  </ul>
                )}
              </li>
            )}
          </ul>

          {/* Button mở menu trên mobile */}
          {responsive && (
            <div className="right-btns">
              <button className="btn menu-toggle" onClick={() => setShowSide(true)}>
                <i className="ri-menu-3-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
